import { useApiFormErrors } from "@/hooks/useApiFormErrors";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Button } from "@hipotecar/ui/button";
import { Input } from "@hipotecar/ui/input";
import {
	Select,
	SelectTrigger,
	SelectValue,
	SelectContent,
	SelectItem,
} from "@/components/ui/select";
import {
	Form,
	FormField,
	FormItem,
	FormLabel,
	FormControl,
	FormMessage,
	FormDescription,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { z } from "zod";
import type { ApiValidationError } from "../../schemas";
import { creditScoreRangeSchema, preApprovalStatusSchema } from "../../schemas";

// Financial profile form schema
const financialProfileSchema = z.object({
	credit_score_range: creditScoreRangeSchema.optional(),
	debt_to_income_ratio: z.coerce.number().min(0).max(100).optional(),
	first_time_homebuyer: z.boolean(),
	pre_approval_status: preApprovalStatusSchema.optional(),
});

type FinancialProfileFormData = z.infer<typeof financialProfileSchema>;

interface FinancialProfileFormProps {
	initialData?: Partial<FinancialProfileFormData>;
	onSubmit: (data: FinancialProfileFormData) => Promise<void> | void;
	onCancel: () => void;
	isLoading?: boolean;
	apiErrors?: ApiValidationError[];
}

export default function FinancialProfileForm({
	initialData,
	onSubmit,
	onCancel,
	isLoading = false,
	apiErrors,
}: FinancialProfileFormProps) {
	const { t } = useTranslation(["common", "profile"]);

	const form = useForm<FinancialProfileFormData>({
		resolver: zodResolver(financialProfileSchema),
		defaultValues: initialData || {
			credit_score_range: "fair",
			debt_to_income_ratio: 0,
			first_time_homebuyer: false,
			pre_approval_status: "not_started",
		},
	});

	useApiFormErrors(form, apiErrors);

	const onFormSubmit = async (data: FinancialProfileFormData) => {
		await onSubmit(data);
	};

	const creditScoreOptions = [
		{ value: "excellent", label: t("profile:creditScoreOptions.excellent") },
		{ value: "good", label: t("profile:creditScoreOptions.good") },
		{ value: "fair", label: t("profile:creditScoreOptions.fair") },
		{ value: "poor", label: t("profile:creditScoreOptions.poor") },
		{ value: "very_poor", label: t("profile:creditScoreOptions.very_poor") },
	];

	const preApprovalOptions = [
		{
			value: "not_started",
			label: t("profile:preApprovalOptions.not_started"),
		},
		{
			value: "in_progress",
			label: t("profile:preApprovalOptions.in_progress"),
		},
		{
			value: "pre_approved",
			label: t("profile:preApprovalOptions.pre_approved"),
		},
		{ value: "approved", label: t("profile:preApprovalOptions.approved") },
	];

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(onFormSubmit)}
				className="flex flex-col"
			>
				<div className="custom-scrollbar max-h-[600px] space-y-4 overflow-y-auto px-2 pb-3">
					<FormField
						control={form.control}
						name="credit_score_range"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t("profile:fields.creditScore")}</FormLabel>
								<Select
									onValueChange={field.onChange}
									value={field.value ?? ""}
									disabled={isLoading}
								>
									<FormControl>
										<SelectTrigger>
											<SelectValue
												placeholder={
													t("profile:fields.creditScore") || "Seleccionar..."
												}
											/>
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{creditScoreOptions.map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="debt_to_income_ratio"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t("profile:fields.debtToIncome")}</FormLabel>
								<FormControl>
									<Input
										type="number"
										step={0.1}
										{...field}
										value={field.value ?? ""}
										onChange={(e) =>
											field.onChange(
												e.target.value === ""
													? undefined
													: parseFloat(e.target.value),
											)
										}
										disabled={isLoading}
									/>
								</FormControl>
								<FormDescription>
									{t("profile:fields.debtToIncomeHint")}{" "}
									<a
										href="https://www.misentinel.com.pe/usuario/login?cross_source=misentinel"
										target="_blank"
										rel="noopener noreferrer"
										className="text-brand-600 hover:underline"
									>
										miSentinel
									</a>
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="pre_approval_status"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t("profile:fields.preApprovalStatus")}</FormLabel>
								<Select
									onValueChange={field.onChange}
									value={field.value ?? "not_started"}
									disabled={isLoading}
								>
									<FormControl>
										<SelectTrigger>
											<SelectValue
												placeholder={
													t("profile:fields.preApprovalStatus") ||
													"Seleccionar..."
												}
											/>
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{preApprovalOptions.map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="first_time_homebuyer"
						render={({ field }) => (
							<FormItem className="py-2">
								<div className="flex items-center justify-between rounded-lg border border-gray-200 p-4 dark:border-gray-700">
									<label
										htmlFor="first_time_homebuyer"
										className="text-sm font-medium"
									>
										{t("profile:fields.firstTimeHomebuyer")}
									</label>
									<Switch
										id="first_time_homebuyer"
										checked={field.value}
										onCheckedChange={field.onChange}
										disabled={isLoading}
									/>
								</div>
							</FormItem>
						)}
					/>
				</div>

				{/* Form Actions */}
				<div className="mt-6 flex items-center gap-3 px-2 lg:justify-end">
					<Button
						type="button"
						size="sm"
						variant="outline"
						onClick={onCancel}
						disabled={isLoading}
					>
						{t("profile:close")}
					</Button>
					<Button type="submit" size="sm" disabled={isLoading}>
						{isLoading ? t("common:loading") : t("profile:saveChanges")}
					</Button>
				</div>
			</form>
		</Form>
	);
}
